import React, { useContext } from "react";
import { AppContext } from "../App";
import { getCurrentYear } from "../Helpers";
import { FiMail, FiMapPin, FiFacebook, FiInstagram, FiLinkedin, FiYoutube } from "react-icons/fi";
import { FaXTwitter, FaLeaf, FaWpforms, FaArrowRightToBracket } from "react-icons/fa6";
import { MdOutlineArrowOutward } from "react-icons/md";

export default function Footer() {
  const appContext = useContext(AppContext);

  // Mapped navigational routes for Tanzavibe infrastructure
  const navLinks = [
    { label: "Home", item: "home" },
    { label: "About Us", item: "about_us" },
    { label: "Contact Us", item: "contact_us" },
  ];

  return (
    <>
      <style>{`
        .vibe-footer {
          background: linear-gradient(135deg, #0A0F18 0%, #111A2E 100%);
          color: #f8fafc;
          padding: 80px 0 30px;
          font-family: 'Libre Franklin', system-ui, sans-serif;
          border-top: 1px solid rgba(30, 45, 74, 0.6);
        }

        .vibe-footer-grid {
          display: grid;
          grid-template-columns: 1.5fr 1fr 1fr 1.5fr;
          gap: 40px;
          margin-bottom: 60px;
        }

        .vibe-footer-brand img {
          height: 52px;
          margin-bottom: 20px;
          object-fit: contain;
        }

        .vibe-footer-title {
          font-family: 'Cormorant Garamond', serif;
          font-size: 1.4rem;
          letter-spacing: 0.02em;
        }

        .vibe-footer-tagline {
          color: #A0AEC0;
          font-size: 0.9rem;
          line-height: 1.6;
          max-width: 320px;
          margin-bottom: 24px;
        }

        .vibe-social-wrapper {
          display: flex;
          gap: 15px;
        }

        .vibe-social-link {
          color: #718096;
          font-size: 1.2rem;
          transition: all 0.3s ease;
        }

        .vibe-social-link:hover {
          color: #ED8936;
          transform: translateY(-3px);
        }

        .vibe-footer-label {
          color: #ffffff;
          font-weight: 700;
          font-size: 0.75rem;
          text-transform: uppercase;
          letter-spacing: 0.12em;
          margin-bottom: 25px;
          display: block;
        }

        .vibe-footer-list { 
          list-style: none; 
          padding: 0; 
          margin: 0; 
        }
        
        .vibe-footer-list li { 
          margin-bottom: 12px; 
        }
        
        .vibe-footer-link {
          color: #CBD5E0;
          text-decoration: none;
          font-size: 0.9rem;
          transition: all 0.2s ease;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: none;
          border: none;
          cursor: pointer;
          padding: 0;
          text-align: left;
        }

        .vibe-footer-link:hover { 
          color: #ED8936; 
          transform: translateX(4px); 
        }

        .btn-vibe-outline {
          border: 1px solid rgba(30, 45, 74, 0.9);
          background-color: #0A0F18;
          color: #E2E8F0;
          padding: 12px 16px;
          border-radius: 4px;
          font-size: 0.85rem;
          text-decoration: none;
          display: flex;
          align-items: center;
          transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
        }

        .btn-vibe-outline:hover {
          background: rgba(237, 137, 54, 0.08);
          border-color: #ED8936;
          color: #FFF;
          transform: translateY(-2px);
        }

        .vibe-btn-gap {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .vibe-footer-bottom {
          border-top: 1px solid rgba(30, 45, 74, 0.6);
          padding-top: 30px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 0.8rem;
          color: #718096;
        }

        @media (max-width: 1200px) {
          .vibe-footer-grid { grid-template-columns: 1fr 1fr; gap: 50px; }
        }

        @media (max-width: 768px) {
          .vibe-footer-grid { grid-template-columns: 1fr; text-align: center; gap: 40px; }
          .vibe-footer-link, .btn-vibe-outline { justify-content: center; }
          .vibe-footer-tagline { max-width: 100%; }
          .vibe-social-wrapper { justify-content: center; }
          .vibe-footer-bottom { flex-direction: column; gap: 15px; text-align: center; }
        }
      `}</style>

      <footer className="vibe-footer">
        <div className="container-fluid px-5">
          <div className="vibe-footer-grid">
            
            {/* 1. Brand Identity & Profile Summary */}
            <div className="vibe-footer-brand">
              <h5 className="vibe-footer-title fw-bold text-white mb-2">TANZAVIBE HOLDINGS CO. LTD</h5>
              <p className="vibe-footer-tagline">
                Creating sustainable value through agribusiness value-addition, professional water infrastructure, and strategic investments across Africa.
              </p>
            </div>

            {/* 2. Systems Navigation Links */}
            <div>
              <span className="vibe-footer-label">Corporate Navigation</span>
              <ul className="vibe-footer-list">
                {navLinks.map(link => (
                  <li key={link.item}>
                    <button onClick={() => appContext.navTo({ item: link.item })} className="vibe-footer-link">
                      {link.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* 3. Mapped Corporate Registrar Addresses */}
            <div>
              <span className="vibe-footer-label">Head Office</span>
              <ul className="vibe-footer-list">
                <li><a href="mailto:info@tanzavibe.co.tz" className="vibe-footer-link"><FiMail /> info@tanzavibe.co.tz</a></li>
                <li>
                  <a 
                    href="https://www.google.com/maps/search/?api=1&query=Plot+No.+1,+Olkerien,+Arusha,+Tanzania" 
                    target="_blank" 
                    rel="noreferrer" 
                    className="vibe-footer-link"
                  >
                    <FiMapPin /> Plot No. 1, Olkerien Area
                  </a>
                </li>
                <li className="vibe-footer-link text-light ps-0" style={{ cursor: 'default' }}>
                  P.O. Box 15341, Arusha, Tanzania
                </li>
              </ul>
            </div>

            {/* 4. Access Portals & Operational Action Links */}
            <div>
              <span className="vibe-footer-label">Operations & Inquiries</span>
              <div className="d-flex flex-column gap-2">
                <a onClick={() => appContext.navTo({item:'services'})} className="btn-vibe-outline">
                  <div className="vibe-btn-gap">
                    <FaLeaf size={14} style={{ color: '#38A169' }} /> 
                    <span>Agribusiness Division</span>
                  </div>
                  <MdOutlineArrowOutward size={16} className="ms-auto" style={{ color: '#718096' }} />
                </a>

                <a onClick={() => appContext.navTo({item:'services'})} className="btn-vibe-outline">
                  <div className="vibe-btn-gap">
                    <FaArrowRightToBracket size={14} style={{ color: '#ED8936' }} /> 
                    <span>Infrastructure Solutions</span>
                  </div>
                  <MdOutlineArrowOutward size={16} className="ms-auto" style={{ color: '#718096' }} />
                </a>

                <a onClick={() => appContext.navTo({item:'contact_us'})} className="btn-vibe-outline">
                  <div className="vibe-btn-gap">
                    <FaWpforms size={14} style={{ color: '#CBD5E0' }} /> 
                    <span>Request Technical Survey</span>
                  </div>
                  <MdOutlineArrowOutward size={16} className="ms-auto" style={{ color: '#718096' }} />
                </a>
              </div>
            </div>

          </div>

          {/* Bottom Copyright & Tech Partner Layout */}
          <div className="vibe-footer-bottom">
            <p className="mb-0">© {getCurrentYear()} Tanzavibe Holdings Company Limited. All Rights Reserved.</p>
            <div className="d-flex gap-1">
              Developed By <a href="https://werenium.com" target="_blank" rel="noreferrer" className="text-decoration-none text-reset fw-bold" style={{ color: '#ED8936' }}>Werenium Technologies</a>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}